import React, { useCallback, useState } from 'react';
import { RiDragMove2Line, RiEyeLine, RiEyeOffLine, RiLockFill } from 'react-icons/ri';

import useLayersPanel from '../../hooks/useLayersPanel';
import { DeleteOutline } from '../../utils/editorReactIcons';
import { WATERMARK_ANNOTATION_ID } from '../../utils/constants';
import {
  StyledLayerDeleteButton,
  StyledLayerDragHandle,
  StyledLayerLockBadge,
  StyledLayerRow,
  StyledLayerRowLabel,
  StyledLayerVisibilityButton,
  StyledLayersList,
  StyledLayersPanel,
  StyledLayersPanelEmpty,
  StyledLayersPanelTitle,
} from './LayersPanel.styled';

const LayersPanel = ({ t, showTitle = true }) => {
  const {
    layers,
    layerCount,
    selectLayer,
    toggleLayerVisibility,
    removeLayer,
    moveLayerToDisplayIndex,
  } = useLayersPanel();

  const [draggingId, setDraggingId] = useState(null);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);

  const handleRowClick = useCallback(
    (layerId, event) => {
      const multiple =
        event.shiftKey || event.metaKey || event.ctrlKey;
      selectLayer(layerId, { multiple });
    },
    [selectLayer],
  );

  const handleDragStart = useCallback((event, layer) => {
    if (layer.isLocked) {
      event.preventDefault();
      return;
    }
    setDraggingId(layer.id);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', layer.id);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    setDropTargetIndex(null);
  }, []);

  const handleDragOverRow = useCallback((event, displayIndex) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDropTargetIndex(displayIndex);
  }, []);

  const handleDropOnRow = useCallback(
    (event, displayIndex) => {
      event.preventDefault();
      const layerId =
        event.dataTransfer.getData('text/plain') || draggingId;
      setDraggingId(null);
      setDropTargetIndex(null);

      if (!layerId) {
        return;
      }

      moveLayerToDisplayIndex(layerId, displayIndex);
    },
    [draggingId, moveLayerToDisplayIndex],
  );

  const handleVisibilityClick = useCallback(
    (event, layerId) => {
      event.stopPropagation();
      toggleLayerVisibility(layerId);
    },
    [toggleLayerVisibility],
  );

  const handleDeleteClick = useCallback(
    (event, layer) => {
      event.stopPropagation();
      removeLayer(layer.id);
    },
    [removeLayer],
  );

  return (
    <StyledLayersPanel className="respark_layers-panel">
      {showTitle ? (
        <StyledLayersPanelTitle>{t('layersPanelTitle')}</StyledLayersPanelTitle>
      ) : null}
      {layerCount === 0 ? (
        <StyledLayersPanelEmpty>{t('layersPanelEmpty')}</StyledLayersPanelEmpty>
      ) : (
        <StyledLayersList className="respark_layers-panel-list">
          {layers.map((layer, displayIndex) => (
            <StyledLayerRow
              key={layer.id}
              className="respark_layers-panel-row"
              data-selected={layer.isSelected ? 'true' : 'false'}
              data-dragging={draggingId === layer.id ? 'true' : 'false'}
              data-drop-target={
                dropTargetIndex === displayIndex ? 'true' : 'false'
              }
              draggable={!layer.isLocked}
              onClick={(event) => handleRowClick(layer.id, event)}
              onDragStart={(event) => handleDragStart(event, layer)}
              onDragEnd={handleDragEnd}
              onDragOver={(event) => handleDragOverRow(event, displayIndex)}
              onDrop={(event) => handleDropOnRow(event, displayIndex)}
            >
              <StyledLayerDragHandle
                data-disabled={layer.isLocked ? 'true' : 'false'}
                title={
                  layer.isLocked
                    ? t('layersPanelDragDisabledLocked')
                    : t('layersPanelDragHint')
                }
                aria-hidden
              >
                <RiDragMove2Line size={16} />
              </StyledLayerDragHandle>
              <StyledLayerRowLabel title={layer.label}>
                {layer.label}
              </StyledLayerRowLabel>
              {layer.isLocked && (
                <StyledLayerLockBadge title={t('layersPanelLocked')}>
                  <RiLockFill size={14} />
                </StyledLayerLockBadge>
              )}
              <StyledLayerVisibilityButton
                type="button"
                data-hidden={layer.isVisible ? 'false' : 'true'}
                title={
                  layer.isVisible
                    ? t('layersPanelHideLayer')
                    : t('layersPanelShowLayer')
                }
                aria-label={
                  layer.isVisible
                    ? t('layersPanelHideLayer')
                    : t('layersPanelShowLayer')
                }
                aria-pressed={layer.isVisible}
                onClick={(event) =>
                  handleVisibilityClick(event, layer.id)}
              >
                {layer.isVisible ? (
                  <RiEyeLine size={18} />
                ) : (
                  <RiEyeOffLine size={18} />
                )}
              </StyledLayerVisibilityButton>
              {layer.id !== WATERMARK_ANNOTATION_ID ? (
                <StyledLayerDeleteButton
                  type="button"
                  title={
                    layer.isLocked
                      ? t('layersPanelDeleteDisabledLocked')
                      : t('layersPanelDeleteLayer')
                  }
                  aria-label={
                    layer.isLocked
                      ? t('layersPanelDeleteDisabledLocked')
                      : t('layersPanelDeleteLayer')
                  }
                  disabled={layer.isLocked}
                  onClick={(event) => handleDeleteClick(event, layer)}
                >
                  <DeleteOutline size={16} />
                </StyledLayerDeleteButton>
              ) : null}
            </StyledLayerRow>
          ))}
        </StyledLayersList>
      )}
    </StyledLayersPanel>
  );
};

export default LayersPanel;
